/* Joffzart PDP: native Shopify product form, exact variant matching, progressive enhancement. */
(() => {
  if (customElements.get('joffz-pdp2')) return;
  class JoffzPdp extends HTMLElement {
    static normalize(value) {
      return String(value || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/ı/g, 'i').toLowerCase();
    }
    static finishKind(value) {
      const name = this.normalize(value);
      const canvas = /canvas|kanvas|leinwand|toile|lienzo|tela/.test(name);
      const colour = /oak|eiche|chene|rovere|mese|roble/.test(name) ? 'oak' : /black|schwarz|noir|nero|siyah|negro/.test(name) ? 'black' : /white|weiss|weiß|blanc|bianc|beyaz/.test(name) ? 'white' : null;
      if (colour) return colour + (canvas ? '-canvas' : '');
      if (canvas) return 'canvas';
      if (/poster|print|impression|baski|druck|stampa|lamina/.test(name)) return 'poster';
      return 'unknown';
    }
    static resolve(variants, options) {
      return variants.find(variant => variant.options.length === options.length && variant.options.every((value, index) => value === options[index])) || null;
    }
    static dimensions(value) {
      const metric = String(value || '').match(/(\d+(?:[.,]\d+)?)\s*[x×]\s*(\d+(?:[.,]\d+)?)\s*cm/i);
      return metric ? metric.slice(1).map(number => Number(number.replace(',', '.'))) : null;
    }
    connectedCallback() {
      if (this.initialized) return;
      try { this.data = JSON.parse(this.querySelector('[data-jp2-config]').textContent); }
      catch (error) { console.warn('Joffzart PDP: native product selector retained.', error); return; }
      this.initialized = true;
      this.controller = new AbortController();
      this.listen = (element, type, callback, options = {}) => element?.addEventListener(type, callback, { ...options, signal: this.controller.signal });
      this.strings = this.data.strings;
      this.listen(document, 'cart:change', event => this.syncMarketPrices(event.detail?.cart));
      this.finishPosition = Number(this.dataset.finishPosition);
      this.sizePosition = Number(this.dataset.sizePosition);
      this.form = this.querySelector('form[data-type="add-to-cart-form"]');
      this.variantSelect = this.form.querySelector('[name="id"]');
      this.atc = this.form.querySelector('[name="add"]');
      this.gallery = this.querySelector('[data-gallery]');
      this.slides = [...this.gallery.children];
      this.slideIndex = 0;
      this.galleryInteracted = false;
      this.gallery.scrollLeft = 0;
      this.resetGallery = () => {
        clearTimeout(this.slideSettle);
        this.pendingSlide = null;
        this.gallery.scrollTo({ left: 0, top: 0, behavior: 'instant' });
        this.setSlide(0);
      };
      this.listen(window, 'pageshow', event => {
        if (event.persisted) this.galleryInteracted = false;
        if (!this.galleryInteracted) this.resetGallery();
        requestAnimationFrame(() => { if (this.isConnected && !this.galleryInteracted) this.resetGallery(); });
      });
      this.reducedMotion = matchMedia('(prefers-reduced-motion: reduce)');
      this.wall = { x: 50, y: 35, scale: 100, tilt: 0, room: 'warm' };
      const urlVariant = new URL(location.href).searchParams.get('variant');
      this.variant = this.data.variants.find(item => String(item.id) === urlVariant) || this.data.variants.find(item => item.id === this.data.selected);
      this.listen(this, 'click', event => this.onClick(event));
      this.listen(this.form, 'submit', event => this.beforeSubmit(event), { capture: true });
      this.listen(this.variantSelect, 'change', () => {
        const variant = this.data.variants.find(item => String(item.id) === this.variantSelect.value);
        if (variant) this.selectVariant(variant, true);
      });
      this.listen(window, 'popstate', () => {
        const id = new URL(location.href).searchParams.get('variant');
        const variant = this.data.variants.find(item => String(item.id) === id) || this.data.variants.find(item => item.id === this.data.selected);
        if (variant) this.selectVariant(variant, false);
      });
      this.listen(this.gallery, 'keydown', event => {
        if (event.target !== this.gallery || !['ArrowLeft', 'ArrowRight'].includes(event.key)) return;
        event.preventDefault();
        this.goToSlide(this.slideIndex + (event.key === 'ArrowRight' ? 1 : -1));
      });
      const settleGallery = () => {
        clearTimeout(this.slideSettle);
        const width=this.gallery.clientWidth;
        if(!width)return;
        const index=Math.max(0,Math.min(this.slides.length-1,Math.round(this.gallery.scrollLeft/width)));
        if (this.pendingSlide != null && Math.abs(this.gallery.scrollLeft - this.pendingSlide * width) > 2) return;
        this.pendingSlide=null;
        this.setSlide(index);
      };
      this.listen(this.gallery,'scroll',()=>{clearTimeout(this.slideSettle);this.slideSettle=setTimeout(settleGallery,140);},{passive:true});
      this.listen(this.gallery,'scrollend',settleGallery);
      this.listen(this.gallery,'pointerdown',()=>{this.galleryInteracted=true;this.pendingSlide=null;},{passive:true});
      this.querySelectorAll('dialog').forEach(dialog => {
        this.listen(dialog, 'cancel', event => { event.preventDefault(); this.closeDialog(); });
        this.listen(dialog, 'click', event => { if (event.target === dialog) this.closeDialog(); });
      });
      this.querySelectorAll('.jp2__accordions summary').forEach(summary => this.listen(summary, 'click', event => this.toggleDetails(event, summary)));
      this.setupWall();
      this.listen(this.form.querySelector('[data-family-photo]'), 'change', event => {
        const output = this.form.querySelector('[data-family-filename]');
        if (output) output.textContent = event.target.files[0]?.name || output.dataset.emptyLabel;
      });
      this.selectVariant(this.variant, false, true);
      this.checkMarketPrices();
      this.classList.add('is-ready');
      this.setSlide(0);
      this.galleryResize = new ResizeObserver(([entry]) => {
        const width = Math.round(entry.contentRect.width);
        if (!width || width === this.galleryWidth) return;
        this.galleryWidth = width;
        clearTimeout(this.slideSettle);
        this.pendingSlide = null;
        this.gallery.scrollTo({ left: this.slideIndex * this.gallery.clientWidth, top: 0, behavior: 'instant' });
      });
      this.galleryResize.observe(this.gallery);
      try {
        if (!this.closest('quick-add-modal') || this.data.recentQuick === true) {
          const now = Date.now();
          const lifetime = Number(this.data.recentLifetime) * 86400000;
          const recent = JSON.parse(localStorage.getItem('__sf_theme_recently') || '[]').filter(item => item.productId != this.data.id && now - item.timestamp < lifetime);
          recent.unshift({ productId: String(this.data.id), timestamp: now });
          localStorage.setItem('__sf_theme_recently', JSON.stringify(recent));
        }
      } catch (_) {} 
    }
    disconnectedCallback() {
      this.controller?.abort();
      this.slideObserver?.disconnect();
      this.galleryResize?.disconnect();
      clearTimeout(this.slideSettle);
      this.detailsAnimations?.forEach(animation => animation.cancel());
      clearTimeout(this.closeTimer);
      this.stopVideos();
      if (this.activeDialog) { this.activeDialog.close(); this.unlockPage(); }
      if (this.uploadUrl) URL.revokeObjectURL(this.uploadUrl);
      this.initialized = false;
    }
    text(key) { return this.money(this.strings[key] || key); }
    money(value) {
      const decoder = document.createElement('textarea');
      decoder.innerHTML = value || '';
      return decoder.value;
    }
    price(cents) {
      try { return new Intl.NumberFormat(this.data.locale || document.documentElement.lang, { style:'currency', currency:this.data.currency, currencyDisplay:'narrowSymbol', minimumFractionDigits:0, maximumFractionDigits:0 }).format(Math.trunc(Number(cents) / 100)); }
      catch (_) { return this.money(this.variant?.money || ''); }
    }
    async checkMarketPrices() {
      if (!this.data.cartUrl) return;
      try {
        const response=await fetch(this.data.cartUrl+'.js',{credentials:'same-origin',cache:'no-store',signal:this.controller.signal});
        if(response.ok) await this.syncMarketPrices(await response.json());
      } catch (_) { /* Exact server-rendered prices remain available offline. */ }
    }
    async syncMarketPrices(cart) {
      if (!cart?.currency || cart.currency===this.data.currency || this.pricingRefresh || !this.data.productUrl) return;
      this.pricingRefresh=true;
      try {
        const response=await fetch(this.data.productUrl+'.js',{credentials:'same-origin',cache:'no-store',signal:this.controller.signal});
        if(!response.ok) return;
        const product=await response.json();
        if(String(product.id)!==String(this.data.id)) return;
        for(const row of this.data.variants){ const native=product.variants.find(v=>String(v.id)===String(row.id));if(native){row.cents=native.price;row.compareCents=native.compare_at_price||0;} }
        this.data.currency=cart.currency;
        this.selectVariant(this.variant,false,true);
      } catch (_) { /* Retain the native variant and its price on a network failure. */ }
      finally { this.pricingRefresh=false; }
    }
    optionLabel(value, position) {
      if (position === this.finishPosition) {
        const kind = JoffzPdp.finishKind(value);
        if (kind !== 'unknown') return this.text('finish_' + kind);
      }
      if (position === this.sizePosition && !['US', 'CA'].includes(this.data.country)) {
        const dimensions = JoffzPdp.dimensions(value);
        if (dimensions) return dimensions.map(number => new Intl.NumberFormat(document.documentElement.lang || 'en', { maximumFractionDigits: 2 }).format(number)).join(' × ') + ' cm';
      }
      return value;
    }
    onClick(event) {
      const control = event.target.closest('button');
      if (!control || !this.contains(control) || control.disabled) return;
      if (control.hasAttribute('data-close')) return void this.closeDialog();
      if (control.hasAttribute('data-open-option')) {
        if (!this.atc.classList.contains('loading')) this.openOptions(Number(control.dataset.openOption), control);
        return;
      }
      if (control.dataset.open === 'wall') {
        this.updateWall();
        this.openDialog('wall', control);
      } else if (control.dataset.open) {
        this.openDialog(control.dataset.open, control);
      } else if (control.hasAttribute('data-open-video')) {
        this.querySelectorAll('[data-video]').forEach(item => item.hidden = item.dataset.video !== control.dataset.openVideo);
        this.openDialog('video', control);
        const video = this.querySelector('[data-video="' + control.dataset.openVideo + '"] video');
        video?.play().catch(() => {});
      } else if (control.hasAttribute('data-option-choice')) {
        const options = [...this.variant.options];
        options[this.openPosition] = control.dataset.optionChoice;
        const variant = JoffzPdp.resolve(this.data.variants, options);
        if (!variant?.available) return;
        this.selectVariant(variant, true);
        this.closeDialog();
      } else if (control.hasAttribute('data-choose-frame')) {
        this.closeDialog().then(() => this.openOptions(this.finishPosition, this.atc));
      } else if (control.hasAttribute('data-without-frame')) {
        if (this.continuing) return;
        this.continuing = true;
        this.closeDialog().then(() => {
          this.skipUpgradeOnce = true;
          this.form.requestSubmit(this.atc);
          this.continuing = false;
        });
      } else if (control.hasAttribute('data-gallery-to')) {
        this.goToSlide(Number(control.dataset.galleryTo));
      } else if (control.hasAttribute('data-gallery-next')) {
        this.goToSlide(this.slideIndex + 1);
      } else if (control.hasAttribute('data-gallery-prev')) {
        this.goToSlide(this.slideIndex - 1);
      } else if (control.hasAttribute('data-room-choice')) {
        this.setRoom(control.dataset.roomChoice);
      } else if (control.hasAttribute('data-wall-color')) {
        if (this.wall.room === 'upload') return;
        this.querySelector('[data-wall-scene]').style.setProperty('--wall-tint', control.dataset.wallColor);
        this.querySelectorAll('[data-wall-color]').forEach(chip => chip.setAttribute('aria-pressed', chip === control));
      } else if (control.hasAttribute('data-wall-reset')) {
        this.wall = { x: 50, y: 35, scale: 100, tilt: 0, room: 'warm' };
        this.querySelector('[data-wall-scale]').value = 100;
        this.querySelector('[data-wall-tilt]').value = 0;
        this.querySelector('[data-room-upload]').value = '';
        this.querySelector('[data-wall-scene]').style.removeProperty('--wall-tint');
        this.setRoom('warm');
        this.querySelectorAll('[data-wall-color]').forEach((chip, index) => chip.setAttribute('aria-pressed', index === 0));
        this.querySelector('[data-wall-message]').textContent = '';
      }
    }
    selectVariant(variant, updateUrl, initial = false) {
      if (!variant) return;
      this.variant = variant;
      this.variantSelect.value = String(variant.id);
      const marketReturn = this.querySelector('[data-jp2-market-return]');
      if (marketReturn) {
        const target = new URL(marketReturn.value, location.origin);
        target.searchParams.set('variant', variant.id);
        marketReturn.value = target.pathname + target.search;
      }
      this.querySelector('[data-price]').textContent = this.price(variant.cents);
      const compare = this.querySelector('[data-compare]');
      compare.hidden = !(variant.compareCents > variant.cents);
      compare.textContent = this.price(variant.compareCents);
      this.atc.disabled = !variant.available;
      this.atc.setAttribute('aria-disabled', String(!variant.available));
      this.atc.querySelector('[data-atc-label]').textContent = variant.available ? this.text('add') + ' · ' + this.price(variant.cents) : this.text('sold_out');
      const quantity = this.form.querySelector('[name="quantity"]');
      quantity.value = String(variant.minimum || 1);
      this.querySelectorAll('[data-option-value]').forEach(element => {
        const position = Number(element.dataset.optionValue);
        const label = element.querySelector('span');
        if (label) label.textContent = this.optionLabel(variant.options[position], position);
        else element.textContent = this.optionLabel(variant.options[position], position);
      });
      const kind = JoffzPdp.finishKind(variant.options[this.finishPosition]);
      this.querySelectorAll('[data-swatch]').forEach(swatch => swatch.dataset.swatch = kind);
      const selectedFinish = this.querySelector('[data-selected-finish-image]');
      if (selectedFinish) selectedFinish.src = this.data.images[kind] || this.data.images[kind.split('-')[0]] || this.data.images.poster;
      const savedArt = this.querySelector('[data-wish-toggle]');
      if (savedArt) savedArt.dataset.variant = String(variant.id);
      this.querySelectorAll('.jp2__slide--art [data-jp2-art], [data-wall-placement] [data-jp2-art]').forEach(art => art.dataset.finish = kind === 'unknown' ? 'poster' : kind);
      this.querySelector('[data-material-copy]').textContent = this.text(kind.endsWith('-canvas') ? 'framed_canvas_body' : kind === 'canvas' ? 'canvas_body' : ['oak', 'black', 'white'].includes(kind) ? 'frame_detail' : 'poster_body');
      const delivery = this.querySelector('joffz-delivery-estimator');
      if (delivery) delivery.dataset.available = String(variant.available);
      this.updateWall();
      if (!initial) {
        const imageIndex = this.dataset.cleanArt === 'true' ? 0 : this.slides.findIndex(slide => slide.dataset.slide === String(variant.imageId));
        if (imageIndex >= 0) this.goToSlide(imageIndex, true);
        this.dispatchEvent(new CustomEvent('variant:change', { bubbles: true, detail: { variant: { id: variant.id, available: variant.available, options: variant.options, price: variant.cents, compare_at_price: variant.compareCents }, productId: this.data.id, sectionId: this.dataset.section } }));
      }
      if (updateUrl && !this.closest('quick-add-modal')) {
        const url = new URL(location.href);
        url.searchParams.set('variant', variant.id);
        history.replaceState({ ...history.state, joffzVariant: variant.id }, '', url);
      }
    }
    frameVariants() {
      if (this.finishPosition < 0) return [];
      return this.data.variants.filter(item => item.available && ['oak', 'white', 'black'].includes(JoffzPdp.finishKind(item.options[this.finishPosition])) && item.options.every((value, index) => index === this.finishPosition || value === this.variant.options[index]));
    }
    beforeSubmit(event) {
      if (this.skipUpgradeOnce) { this.skipUpgradeOnce = false; return; }
      if (!this.variant.available || this.atc.classList.contains('loading')) return;
      const family = this.form.querySelector('[data-family-personalization]');
      if (family && (!family.querySelector('[data-family-photo]').files.length || !family.querySelector('[data-family-name]').value.trim())) return;
      const frames = this.frameVariants();
      if (JoffzPdp.finishKind(this.variant.options[this.finishPosition]) !== 'poster' || !frames.length) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      const preview = this.querySelector('[data-dialog="upgrade"] [data-jp2-art]');
      if (preview) preview.dataset.finish = JoffzPdp.finishKind(frames[0].options[this.finishPosition]);
      this.openDialog('upgrade', this.atc);
    }
    async openOptions(position, opener) {
      if (position < 0 || !this.data.options[position]) return;
      this.openPosition = position;
      this.querySelector('[data-drawer-title]').textContent = position === this.finishPosition ? this.text('finish') : position === this.sizePosition ? this.text('size') : this.data.options[position].name;
      const service = this.querySelector('[data-option-frame-service]');
      if (service) service.hidden = position !== this.finishPosition || !this.frameVariants().length;
      const list = this.querySelector('[data-option-list]');
      list.replaceChildren();
      this.data.options[position].values.forEach(value => {
        const options = [...this.variant.options];
        options[position] = value;
        const variant = JoffzPdp.resolve(this.data.variants, options);
        const row = document.createElement('button');
        row.type = 'button';
        row.className = 'jp2__option' + (position === this.finishPosition ? ' jp2__option--finish' : '');
        row.dataset.optionChoice = value;
        row.disabled = !variant?.available;
        row.setAttribute('aria-pressed', String(value === this.variant.options[position]));
        if (position === this.finishPosition) {
          const kind = JoffzPdp.finishKind(value);
          const image = document.createElement('img');
          image.className = 'jp2__finish-image';
          image.src = this.data.images[kind] || this.data.images.poster;
          image.width = 58;
          image.height = 58;
          image.loading = 'lazy';
          image.alt = '';
          row.append(image);
        }
        const title = document.createElement('span');
        title.textContent = this.optionLabel(value, position);
        const price = document.createElement('span');
        price.className = 'jp2__option-price';
        if (variant?.compareCents > variant?.cents) {
          const reference = document.createElement('s');
          reference.textContent = this.price(variant.compareCents);
          price.append(reference);
        }
        const current = document.createElement('span');
        current.textContent = variant ? this.price(variant.cents) : '';
        price.append(current);
        if (!variant?.available) {
          const note = document.createElement('small');
          note.textContent = variant ? this.text('sold_out') : this.text('unavailable');
          price.append(note);
        }
        row.append(title, price);
        list.append(row);
      });
      await this.openDialog('options', opener);
    }
    async openDialog(name, opener) {
      if (this.activeDialog) await this.closeDialog();
      const dialog = this.querySelector('[data-dialog="' + name + '"]');
      if (!dialog) return;
      this.activeDialog = dialog;
      this.dialogOpener = opener;
      this.stopVideos();
      this.previousOverflow = document.body.style.overflow;
      this.scrollLock = {x:window.scrollX,y:window.scrollY,style:document.body.getAttribute('style')};
      Object.assign(document.body.style,{position:'fixed',top:-this.scrollLock.y+'px',left:'0',width:'100%',overflow:'hidden'});
      dialog.showModal();
      dialog.querySelector('[data-close]')?.focus({preventScroll:true});
      requestAnimationFrame(() => requestAnimationFrame(() => { if (dialog.open) dialog.classList.add('is-visible'); }));
    }
    closeDialog() {
      const dialog = this.activeDialog;
      if (!dialog) return Promise.resolve();
      if (this.closingPromise) return this.closingPromise;
      dialog.classList.remove('is-visible');
      this.stopVideos(dialog);
      this.closingPromise = new Promise(resolve => {
        this.closeTimer = setTimeout(() => {
          dialog.close();
          this.unlockPage();
          this.activeDialog = null;
          this.dialogOpener?.focus({ preventScroll: true });
          this.closingPromise = null;
          resolve();
        }, this.reducedMotion.matches ? 0 : dialog.classList.contains('jp2__dialog--drawer') ? 440 : 300);
      });
      return this.closingPromise;
    }
    unlockPage() {
      if (!this.scrollLock) return;
      const lock = this.scrollLock;
      if (lock.style === null) document.body.removeAttribute('style');
      else document.body.setAttribute('style', lock.style);
      window.scrollTo({left:lock.x,top:lock.y,behavior:'instant'});
      this.scrollLock = null;
    }
    toggleDetails(event, summary) {
      event.preventDefault();
      const details = summary.parentElement;
      const content = details.querySelector('.jp2__details-content');
      if (this.reducedMotion.matches || !content?.animate) { details.open = !details.open; return; }
      this.detailsAnimations ||= new Map();
      const previous = this.detailsAnimations.get(details);
      const startHeight = details.getBoundingClientRect().height;
      const summaryHeight = summary.getBoundingClientRect().height;
      const opening = previous ? details.dataset.targetOpen !== 'true' : !details.open;
      previous?.cancel();
      details.dataset.targetOpen = String(opening);
      details.open = true;
      content.style.overflow = 'hidden';
      const animation = details.animate([{ height: startHeight + 'px' }, { height: (summaryHeight + (opening ? content.scrollHeight : 0)) + 'px' }], { duration: 300, easing: 'cubic-bezier(.4,0,.2,1)' });
      this.detailsAnimations.set(details, animation);
      animation.finished.then(() => {
        if (this.detailsAnimations.get(details) !== animation) return;
        details.open = opening;
        content.style.removeProperty('overflow');
        this.detailsAnimations.delete(details);
      }).catch(() => {});
    }
    goToSlide(index, instant = false) {
      this.galleryInteracted = true;
      index = Math.max(0, Math.min(index, this.slides.length - 1));
      this.pendingSlide = index;
      clearTimeout(this.slideSettle);
      this.slideSettle = setTimeout(() => { this.pendingSlide = null; this.setSlide(Math.round(this.gallery.scrollLeft / this.gallery.clientWidth)); }, 700);
      this.gallery.scrollTo({ left: index * this.gallery.clientWidth, behavior: instant || this.reducedMotion.matches ? 'instant' : 'smooth' });
      this.setSlide(index);
    }
    setSlide(index) {
      index = Math.max(0, Math.min(index, this.slides.length - 1));
      const changed = this.slideIndex !== index;
      this.slideIndex = index;
      for (const neighbour of this.slides.slice(Math.max(0,index-1),index+3)) {
        const image=neighbour.querySelector('img');
        if(image){image.loading='eager';image.decoding='async';}
      }
      const counter = this.querySelector('[data-gallery-count]');
      if (counter) counter.textContent = (index + 1) + ' / ' + this.slides.length;
      this.querySelector('[data-gallery-prev]').disabled = index === 0;
      this.querySelector('[data-gallery-next]').disabled = index === this.slides.length - 1;
      this.slides.forEach((slide, number) => { if (number !== index) this.stopVideos(slide); });
    }
    stopVideos(container = this) {
      container.querySelectorAll('video').forEach(video => video.pause());
      container.querySelectorAll('iframe').forEach(frame => {
        // Pause supported embeds without reloading them or dropping their selection.
        if (/youtube(?:-nocookie)?\.com/.test(frame.src)) frame.contentWindow?.postMessage('{"event":"command","func":"pauseVideo","args":""}', new URL(frame.src).origin);
        if (/player\.vimeo\.com/.test(frame.src)) frame.contentWindow?.postMessage('{"method":"pause"}', new URL(frame.src).origin);
      });
    }
    setupWall() {
      const scene = this.querySelector('[data-wall-scene]');
      if (!scene) return;
      const placement = this.querySelector('[data-wall-placement]');
      this.listen(this.querySelector('[data-wall-scale]'), 'input', event => { this.wall.scale = Number(event.target.value); this.updateWall(); });
      this.listen(this.querySelector('[data-wall-tilt]'), 'input', event => { this.wall.tilt = Number(event.target.value); this.updateWall(); });
      this.listen(this.querySelector('[data-room-upload]'), 'change', event => {
        const file = event.target.files[0];
        if (!file) return;
        if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type) || file.size > 20 * 1024 * 1024) {
          this.querySelector('[data-wall-message]').textContent = this.text('file_error');
          event.target.value = '';
          return;
        }
        const version = this.uploadSequence = (this.uploadSequence || 0) + 1;
        const url = URL.createObjectURL(file);
        const image = new Image();
        image.onload = () => {
          if (!this.isConnected || version !== this.uploadSequence) { URL.revokeObjectURL(url); return; }
          if (this.uploadUrl) URL.revokeObjectURL(this.uploadUrl);
          this.uploadUrl = url;
          this.wall.room = 'upload';
          this.wall.scale = 100;
          this.querySelector('[data-wall-scale]').max = 300;
          this.querySelector('[data-wall-scale]').value = 100;
          this.querySelectorAll('[data-wall-color]').forEach(chip => chip.disabled = true);
          scene.dataset.room = 'upload';
          scene.style.backgroundImage = 'url("' + url + '")';
          this.querySelectorAll('[data-room-choice]').forEach(button => button.setAttribute('aria-pressed', 'false'));
          this.querySelector('[data-wall-message]').textContent = '';
          this.updateWall();
        };
        image.onerror = () => { URL.revokeObjectURL(url); this.querySelector('[data-wall-message]').textContent = this.text('file_error'); };
        image.src = url;
      });
      this.listen(placement, 'pointerdown', event => {
        if (event.button !== 0) return;
        this.dragging = { x: event.clientX, y: event.clientY, left: this.wall.x, top: this.wall.y };
        placement.setPointerCapture(event.pointerId);
      });
      this.listen(placement, 'pointermove', event => {
        if (!this.dragging) return;
        const bounds = scene.getBoundingClientRect();
        this.wall.x = Math.max(8, Math.min(92, this.dragging.left + (event.clientX - this.dragging.x) / bounds.width * 100));
        this.wall.y = Math.max(8, Math.min(92, this.dragging.top + (event.clientY - this.dragging.y) / bounds.height * 100));
        this.updateWall();
      });
      ['pointerup', 'pointercancel', 'lostpointercapture'].forEach(name => this.listen(placement, name, () => this.dragging = null));
      this.listen(placement, 'keydown', event => {
        const moves = { ArrowLeft: [-2, 0], ArrowRight: [2, 0], ArrowUp: [0, -2], ArrowDown: [0, 2] };
        if (!moves[event.key]) return;
        event.preventDefault();
        this.wall.x = Math.max(8, Math.min(92, this.wall.x + moves[event.key][0]));
        this.wall.y = Math.max(8, Math.min(92, this.wall.y + moves[event.key][1]));
        this.updateWall();
      });
    }
    setRoom(name) {
      this.uploadSequence = (this.uploadSequence || 0) + 1;
      this.querySelectorAll('[data-wall-color]').forEach(chip => chip.disabled = false);
      this.querySelector('[data-wall-scale]').max = 180;
      this.wall.scale = Math.min(this.wall.scale, 180);
      this.querySelector('[data-wall-scale]').value = this.wall.scale;
      this.wall.room = name;
      const scene = this.querySelector('[data-wall-scene]');
      scene.dataset.room = name;
      scene.style.removeProperty('background-image');
      if (this.uploadUrl) { URL.revokeObjectURL(this.uploadUrl); this.uploadUrl = null; }
      this.querySelectorAll('[data-room-choice]').forEach(button => button.setAttribute('aria-pressed', String(button.dataset.roomChoice === name)));
      this.updateWall();
    }
    updateWall() {
      const placement = this.querySelector('[data-wall-placement]');
      if (!placement || !this.variant) return;
      const dimensions = JoffzPdp.dimensions(this.variant.options[this.sizePosition]);
      const width = this.wall.room === 'upload' ? 22 : dimensions ? dimensions[0] / 320 * 100 : 20;
      placement.style.left = this.wall.x + '%';
      placement.style.top = this.wall.y + '%';
      placement.style.width = Math.max(5, Math.min(66, width * this.wall.scale / 100)) + '%';
      placement.style.setProperty('--wall-tilt', this.wall.tilt + 'deg');
      const scaleOutput=this.querySelector('[data-wall-scale-output]');
      const tiltOutput=this.querySelector('[data-wall-tilt-output]');
      if(scaleOutput)scaleOutput.value=this.wall.scale+'%';
      if(tiltOutput)tiltOutput.value=this.wall.tilt+'°';
      this.querySelector('[data-wall-selection]').textContent = this.variant.options.map((value, index) => this.optionLabel(value, index)).join(' · ');
    }
  }
  customElements.define('joffz-pdp2', JoffzPdp);
})();
